import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const SegmentForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  // Determine initial type from URL search query if provided (e.g. ?type=1)
  const queryParams = new URLSearchParams(location.search);
  const initialTypeFromQuery = queryParams.get("type");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    segment_name: "",
    type: initialTypeFromQuery !== null ? Number(initialTypeFromQuery) : 0,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchSegmentDetails(id);
    }
  }, [id]);

  const fetchSegmentDetails = async (segmentId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}segment/${segmentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.success && response.data?.data) {
        const item = response.data.data;
        setFormData({
          segment_name: item.segment_name || "",
          type: item.type !== undefined ? Number(item.type) : 0,
        });
      } else {
        showToast.error("Segment details not found.");
        navigate("/research/segment");
      }
    } catch (error) {
      console.error("Error loading segment details:", error);
      showToast.error("Failed to load segment details.");
      navigate("/research/segment");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "type" ? Number(value) : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.segment_name || !formData.segment_name.trim()) {
      newErrors.segment_name = "Segment name is required";
    }
    if (formData.type === undefined || formData.type === null || formData.type === "") {
      newErrors.type = "Type is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showToast.error("Please fill in the required fields.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        segment_name: formData.segment_name.trim(),
        type: Number(formData.type),
      };

      if (id) {
        // Update existing segment
        const response = await axios.put(`${API_URL}segment/${id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.data?.success || response.status === 200) {
          showToast.success("Segment updated successfully!");
          navigate("/research/segment");
        } else {
          showToast.error(response.data?.message || "Failed to update segment.");
        }
      } else {
        // Create new segment
        const response = await axios.post(`${API_URL}segment`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.data?.success || response.status === 201 || response.status === 200) {
          showToast.success("Segment created successfully!");
          navigate("/research/segment");
        } else {
          showToast.error(response.data?.message || "Failed to create segment.");
        }
      }
    } catch (error) {
      console.error("Error saving segment:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to save segment.";
      showToast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Page Title Row */}
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">
              <FontAwesomeIcon icon={faLayerGroup} className="me-2 text-primary" />{" "}
              {id ? "Edit" : "Add"} Segment
            </h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <Link to="/research/segment" className="btn btn-default btn-sm">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Segments
            </Link>
          </div>
        </div>

        {/* Form Panel */}
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">Segment Information</h4>
          </div>
          <div className="panel-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Segment Name */}
                <div className="col-md-6">
                  <div className={`form-group ${errors.segment_name ? "has-error" : ""}`}>
                    <label className="control-label" style={{ fontWeight: "600" }}>
                      Segment Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="segment_name"
                      className="form-control"
                      placeholder="e.g. Equity-Intraday, Thematic, Sector Trackers"
                      value={formData.segment_name}
                      onChange={handleInputChange}
                    />
                    {errors.segment_name && (
                      <span className="help-block text-danger">{errors.segment_name}</span>
                    )}
                  </div>
                </div>

                {/* Type Dropdown */}
                <div className="col-md-6">
                  <div className={`form-group ${errors.type ? "has-error" : ""}`}>
                    <label className="control-label" style={{ fontWeight: "600" }}>
                      Type <span className="text-danger">*</span>
                    </label>
                    <select
                      name="type"
                      className="form-control"
                      value={formData.type}
                      onChange={handleInputChange}
                    >
                      <option value={0}>Streetgains (0 - Credit Based)</option>
                      <option value={1}>Streetfolio (1 - Portfolio Based)</option>
                    </select>
                    {errors.type && (
                      <span className="help-block text-danger">{errors.type}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit & Cancel Buttons */}
              <div className="row mt-3">
                <div className="col-md-12">
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? "Saving..." : id ? "Update Segment" : "Submit"}
                    </button>
                    <Link to="/research/segment" className="btn btn-danger">
                      Cancel
                    </Link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentForm;
